import React, { useRef, useState, useEffect } from 'react';
import { Canvas, useThree, useFrame, useLoader } from '@react-three/fiber';
import { OBJLoader } from 'three/examples/jsm/loaders/OBJLoader';
import { CameraControls, Splat, Center, Html, Text, useProgress } from '@react-three/drei';
import { ModelViewerWrapper, UIOverlay, MapWrapper, MapImage, MapPoint, DebugButton } from './ModelViewer.styled';
import * as THREE from 'three'; 

const ModelViewer = ({
  id,
  modelPath,
  collisionPath,
  maps = [],
  initialCamera = [0, 0, 0],
  initialLookAt,
  rotation = [0, 0, 0],
  position = [0, 0, 0],
  collisionRotation,
  collisionPosition,
  testingMode = false,
  orbitMode = false
}) => {
  
  const cameraControlRef = useRef();
  const [currentMapIndex, setCurrentMapIndex] = useState(0);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [openAnnotationIndex, setOpenAnnotationIndex] = useState(null);
  const activeMap = (maps && maps.length > 0) ? maps[currentMapIndex] : null;

  const [isWalking, setIsWalking] = useState(false);
  const [walkingText, setWalkingText] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);

  const [dynamicAnnotations, setDynamicAnnotations] = useState([]);

  useEffect(() => {
    if (!id) {
      setDynamicAnnotations([]);
      return;
    }

    fetch(`http://localhost:5000/api/annotations/${id}`)
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log("Anotaciones recibidas del backend:", data.data);
          setDynamicAnnotations(data.data);
        } else {
          setDynamicAnnotations([]);
        }
      })
      .catch(error => {
        console.error("Error conectando con el backend:", error);
        setDynamicAnnotations([]);
      });
  }, [id]);

  const walkRef = useRef({
    active: false,
    pathQueue: [],
    currentStart: null,
    currentEnd: null,
    alpha: 0,
    segmentDuration: 0,
    finalTarget: null
  });

  const WALK_SPEED = 1.6;

  const handleLocationChange = (targetLabel, finalCoords, path = []) => {
    if (walkRef.current.active) return;

    setOpenAnnotationIndex(null);
    setCurrentLocation(targetLabel);
    setWalkingText(targetLabel);
    setIsWalking(true);

    const startPos = cameraControlRef.current.camera.position.clone();

    const pathVectors = path.map(p => new THREE.Vector3(p[0], p[1], p[2]));
    const endPos = new THREE.Vector3(finalCoords[0], finalCoords[1], finalCoords[2]);
    pathVectors.push(endPos);

    let targetX, targetY, targetZ;
    
    if (finalCoords.length === 6) {
      targetX = finalCoords[3];
      targetY = finalCoords[4];
      targetZ = finalCoords[5];
    } else {
      targetX = finalCoords[0];
      targetY = finalCoords[1];
      targetZ = finalCoords[2] - 1.0; 
    }

    walkRef.current = {
      active: true,
      phase: 'ROTATE',
      pathQueue: pathVectors,
      currentStart: startPos,
      currentEnd: pathVectors[0],
      alpha: 0,
      segmentDuration: 0,
      finalTarget: new THREE.Vector3(targetX, targetY, targetZ),
      lookStart: null,
      lookEnd: null
    };
  };

  const calcularRutaCompleta = (destinoLabel) => {
    if (!activeMap || !activeMap.navigation || !currentLocation || currentLocation === destinoLabel) return null;

    const grafo = {};
    activeMap.navigation.forEach(nav => {
      if (!grafo[nav.visibleFrom]) grafo[nav.visibleFrom] = [];
      grafo[nav.visibleFrom].push({
        target: nav.targetLabel,
        path: nav.path || [],
        fullNav: nav
      });
    });

    const cola = [[currentLocation]];
    const visitados = new Set([currentLocation]);
    const mapaPadres = {};

    let encontrado = false;

    while (cola.length > 0) {
      const caminoActual = cola.shift();
      const nodoActual = caminoActual[caminoActual.length - 1];

      if (nodoActual === destinoLabel) {
        encontrado = true;
        break;
      }

      if (grafo[nodoActual]) {
        for (const vecino of grafo[nodoActual]) {
          if (!visitados.has(vecino.target)) {
            visitados.add(vecino.target);
            mapaPadres[vecino.target] = { padre: nodoActual, datosConexion: vecino };
            const nuevoCamino = [...caminoActual, vecino.target];
            cola.push(nuevoCamino);
          }
        }
      }
    }

    if (!encontrado) {
      console.warn("No se encontró ruta entre", currentLocation, "y", destinoLabel);
      return null;
    }

    let paso = destinoLabel;
    const pathTotal = [];

    const pasosInversos = [];
    while (paso !== currentLocation) {
      const info = mapaPadres[paso];
      if (!info) break;
      pasosInversos.unshift(info.datosConexion);
      paso = info.padre;
    }

    pasosInversos.forEach(tramo => {
      if (tramo.path) {
        pathTotal.push(...tramo.path);
      }
    });

    return pathTotal;
  };

  useEffect(() => {
    setCurrentMapIndex(0);
    if (maps[0] && maps[0].points && maps[0].points.length > 0) {
      setCurrentLocation(maps[0].points[0].label);
    }
  }, [modelPath]);

  useEffect(() => {
    const aplicarPrimeraPersona = () => {
      if (cameraControlRef.current) {
        const [x, y, z] = initialCamera;
        
        let targetX, targetY, targetZ;

        if (initialLookAt && initialLookAt.length === 3) {
            targetX = initialLookAt[0];
            targetY = initialLookAt[1];
            targetZ = initialLookAt[2];
        } else {
            targetX = 0; targetY = 0; targetZ = 0;
        }

        if (orbitMode) {
            cameraControlRef.current.setLookAt(x, y, z, targetX, targetY, targetZ, false);
        } else {
            const posVec = new THREE.Vector3(x, y, z);
            const lookAtVec = new THREE.Vector3(targetX, targetY, targetZ);
            const direction = new THREE.Vector3().subVectors(lookAtVec, posVec).normalize();
            const targetCercano = posVec.clone().add(direction.multiplyScalar(0.01));

            cameraControlRef.current.setLookAt(
                x, y, z, 
                targetCercano.x, targetCercano.y, targetCercano.z,
                false
            );
        }
      }
    };
    
    aplicarPrimeraPersona();
    const timer = setTimeout(aplicarPrimeraPersona, 500);
    return () => clearTimeout(timer);
  }, [modelPath, initialCamera, initialLookAt, orbitMode]);

  if (!modelPath) return null;

  const moverCamaraA = (coords) => {
    if (!cameraControlRef.current || !coords) return;
    
    let pos, target;

    if (coords.length === 6) {
        pos = new THREE.Vector3(coords[0], coords[1], coords[2]);
        target = new THREE.Vector3(coords[3], coords[4], coords[5]);
    } else {
        pos = new THREE.Vector3(coords[0], coords[1], coords[2]);
        target = new THREE.Vector3(coords[0] + 0.01, coords[1], coords[2] - 0.01); 
    }
    
    const direccion = new THREE.Vector3().subVectors(target, pos).normalize();
    if (direccion.length() === 0) direccion.set(0, 0, -1);
    
    const targetCercano = new THREE.Vector3().copy(pos).add(direccion.multiplyScalar(0.1));

    cameraControlRef.current.setLookAt(
      pos.x, pos.y, pos.z,
      targetCercano.x, targetCercano.y, targetCercano.z,
      true 
    );
  };

  const logCoords = () => {
    if (cameraControlRef.current) {
      const pos = cameraControlRef.current.camera.position;
      console.log(`COPIAR: [${pos.x.toFixed(2)}, ${pos.y.toFixed(2)}, ${pos.z.toFixed(2)}]`);
    }
  };

  return (
    <>
      <ModelViewerWrapper data-testid="ModelViewer">

        <LoadingScreen onFinished={() => setIsLoaded(true)} />

        {walkingText && (
          <div style={{
            position: 'absolute',
            bottom: '15%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            background: 'rgba(0, 0, 0, 0.7)',
            color: 'white',
            padding: '15px 30px',
            borderRadius: '30px',
            fontSize: '18px',
            fontWeight: 'bold',
            letterSpacing: '1px',
            backdropFilter: 'blur(5px)',
            border: '1px solid rgba(255,255,255,0.3)',
            pointerEvents: 'none',
            zIndex: 1000,
            animation: 'pulse 1.5s infinite'
          }}>
            🚶 Caminando a {walkingText}...
          </div>
        )}

        <Canvas flat gl={{ antialias: false }} camera={{ position: initialCamera, fov: 60 }} dpr={1}>
          <color attach="background" args={['#000000']} />
          <Center>
            <Splat src={modelPath} chunkSize={10} rotation={rotation} position={position} />
          </Center>

          {testingMode && activeMap && (
            <PathVisualizer navigation={activeMap.navigation} />
          )}

          {collisionPath && (
            <CollisionMesh 
              url={collisionPath} 
              cameraControlRef={cameraControlRef} 
              testingMode={testingMode}
              rotation={collisionRotation || rotation}
              position={collisionPosition || position}
            />
          )}

          <AnimationLoop 
             walkRef={walkRef} 
             cameraControlRef={cameraControlRef} 
             setIsWalking={setIsWalking} 
             WALK_SPEED={WALK_SPEED}
             setWalkingText={setWalkingText}
          />

          {activeMap && activeMap.annotations && activeMap.annotations.map((note, index) => (
            <InfoLabel 
              key={index} 
              position={note.position} 
              title={note.title}
              content={note.content}
              maxDistance={note.maxDistance}
              maxXDistance={note.maxXDistance}
              maxYDistance={note.maxYDistance}
              maxZDistance={note.maxZDistance}
              testingMode={testingMode}
              isOpen={openAnnotationIndex === index}
              onToggle={() => setOpenAnnotationIndex(openAnnotationIndex === index ? null : index)}
            />
          ))}

          {activeMap && activeMap.navigation && activeMap.navigation.map((nav, index) => {
            if (!testingMode && nav.visibleFrom !== currentLocation) return null;
            
            const foundPoint = activeMap.points.find(p => p.label === nav.targetLabel);
            const coordsDestino = foundPoint ? foundPoint.camPosition : nav.target;
            
            if (!coordsDestino) return null;

            return (
              <NavArrow 
                key={`nav-${index}`}
                position={nav.position}
                rotationY={nav.rotationY || 0}
                label={nav.label}
                onClick={() => handleLocationChange(nav.targetLabel, coordsDestino, nav.path)} 
              />
            );
          })}

          <CameraControls 
            ref={cameraControlRef} 
            makeDefault 
            dollySpeed={orbitMode ? 1 : (testingMode ? 1 : 0)} 
            truckSpeed={orbitMode ? 1 : (testingMode ? 1.5 : 0)}

            azimuthRotateSpeed={testingMode || orbitMode ? 1 : 0.3} 
            polarRotateSpeed={testingMode || orbitMode ? 1 : 0.3} 

            minDistance={orbitMode ? 1.5 : 0} 
            maxDistance={orbitMode ? 20 : Infinity}
          />
          {testingMode && <CameraLogger />}
        </Canvas>
      </ModelViewerWrapper>

      <UIOverlay style={{ 
          opacity: isLoaded ? 1 : 0, 
          pointerEvents: 'none',
          transition: 'opacity 1s ease-out'
      }} >
        {testingMode && (
          <DebugButton onClick={logCoords}>DEBUG: LOG COORDENADAS</DebugButton>
        )}

        {activeMap && (
          <MapWrapper style={{ pointerEvents: 'auto' }}>
            {maps.length > 1 && (
              <div style={{ marginBottom: '10px', textAlign: 'center' }}>
                <select 
                  value={currentMapIndex}
                  onChange={(e) => setCurrentMapIndex(Number(e.target.value))}
                  style={{ padding: '5px', borderRadius: '5px', width: '100%' }}
                >
                  {maps.map((m, i) => <option key={i} value={i}>{m.name}</option>)}
                </select>
              </div>
            )}

            {activeMap.image && (
              <div style={{ position: 'relative' }}>
                <MapImage src={activeMap.image} alt={activeMap.name} />
                
                {activeMap.points.map((punto, index) => (
                  <MapPoint 
                    key={index}
                    $top={punto.top} 
                    $left={punto.left} 
                    data-label={punto.label}
                    onClick={() => {
                      if (currentLocation === punto.label) return;
                      
                      const rutaCompleta = calcularRutaCompleta(punto.label);
                        
                      if (rutaCompleta) {
                        handleLocationChange(punto.label, punto.camPosition, rutaCompleta);
                      } else {
                        handleLocationChange(punto.label, punto.camPosition, []); 
                      }
                    }}
                      style={{ backgroundColor: currentLocation === punto.label ? '#148f14' : '',
                      cursor: currentLocation === punto.label ? 'default' : 'pointer'
                     }}
                  >i</MapPoint>
                ))}
              </div>
            )}
          </MapWrapper>
        )}
      </UIOverlay>
    </>
  );
};

const NavArrow = ({ position, rotationY, onClick, label }) => {
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    document.body.style.cursor = hovered ? 'pointer' : 'auto';
    return () => { document.body.style.cursor = 'auto'; }
  }, [hovered]);

  return (
    <group 
      position={position} 
      rotation={[-Math.PI / 2, 0, rotationY]}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      <mesh>
        <circleGeometry args={[0.4, 32]} />
        <meshBasicMaterial 
          color={hovered ? "#61dafb" : "white"} 
          transparent 
          opacity={hovered ? 0.8 : 0.4} 
          side={THREE.DoubleSide}
        />
      </mesh>

      <Text
        position={[0, 0, 0.05]}
        fontSize={0.4}
        color={hovered ? "black" : "black"}
        anchorX="center"
        anchorY="middle"
        rotation={[0, 0, -Math.PI / 2]}
      >
        ➜
      </Text>
      {hovered && label && (
        <Html position={[0, 0.5, 0]} center>
           <div style={{
             background: 'rgba(0,0,0,0.8)', color: 'white', padding: '4px 8px', 
             borderRadius: '4px', fontSize: '12px', whiteSpace: 'nowrap', pointerEvents: 'none'
           }}>
             {label}
           </div>
        </Html>
      )}
    </group>
  );
};

const InfoLabel = ({ 
  position, title, content, 
  maxDistance = 3, maxYDistance = 5, maxXDistance = 5, maxZDistance = 5, 
  testingMode = false,
  isOpen,
  onToggle
}) => {
  const { camera } = useThree();
  const [isNearby, setIsNearby] = useState(false);

  useFrame(() => {
    const targetVec = new THREE.Vector3(position[0], position[1], position[2]);
    const dist = camera.position.distanceTo(targetVec);
    const distY = Math.abs(camera.position.y - position[1]);
    const distX = Math.abs(camera.position.x - position[0]);
    const distZ = Math.abs(camera.position.z - position[2]);

    const check = dist < maxDistance && distY < maxYDistance && distX < maxXDistance && distZ < maxZDistance;

    if (isNearby !== check) {
      setIsNearby(check);
    }
  });

  if (!isNearby && !isOpen && !testingMode) return null;

  return (
    <group position={position}>
      
      {testingMode && (
        <mesh>
          <sphereGeometry args={[0.15, 16, 16]} /> 
          <meshBasicMaterial color="red" wireframe={true} />
           <Html position={[0, 0.3, 0]} center>
            <div style={{ 
              color: 'red', fontWeight:'bold', fontSize: '12px', 
              background: 'rgba(0,0,0,0.6)', padding: '2px 5px', borderRadius: '4px', whiteSpace: 'nowrap'
            }}>
              ANOTACIÓN: {title}
            </div>
          </Html>
        </mesh>
      )}

      <Html center> 
        <div 
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          style={{ cursor: 'pointer', pointerEvents: 'auto' }}
        >

          {isOpen ? (
            <div style={{
              background: 'rgba(0, 0, 0, 0.9)', color: 'white', padding: '15px 20px',
              borderRadius: '12px', border: '1px solid rgba(255, 255, 255, 0.4)',
              width: '320px', maxWidth: '90vw', textAlign: 'left',
              boxShadow: '0 8px 16px rgba(0,0,0,0.8)',
              animation: 'fadeIn 0.2s ease-out'
            }}>
              <div style={{ position: 'absolute', top: '10px', right: '10px', fontSize: '12px', opacity: 0.7 }}>✖</div>

              {title && <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#61dafb', marginBottom: '8px', paddingRight: '15px' }}>{title}</div>}
              {content && <div>{content}</div>}
            </div>

          ) : (
            (isNearby || isOpen) && (
              <div style={{
                width: '40px', height: '40px', borderRadius: '50%',
                background: 'rgba(255, 255, 255, 0.9)',
                border: '2px solid #61dafb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '24px', boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
                transform: 'translateY(0)', transition: 'transform 0.2s',
                animation: 'bounce 2s infinite'
              }}
              onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
              onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1.0)'}
              >
                📌
              </div>
            )
          )}

        </div>

        <style>{`
          @keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-5px); } }
          @keyframes fadeIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
          @keyframes pulse { 0% { opacity: 0.8; } 50% { opacity: 1; } 100% { opacity: 0.8; } }
        `}</style>
      </Html>
    </group>
  );
};

const PathVisualizer = ({ navigation }) => {
  if (!navigation) return null;

  return (
    <group>
      {navigation.map((nav, i) => (
        <group key={`path-group-${i}`}>
          {nav.path && nav.path.map((point, j) => (
            <mesh key={`p-${i}-${j}`} position={[point[0], point[1], point[2]]}>
              <sphereGeometry args={[0.08, 16, 16]} /> 
              <meshBasicMaterial color="#FFFF00" wireframe={true} />
              
              <Html position={[0, 0.2, 0]} center>
                <div style={{ 
                  color: 'yellow', 
                  fontSize: '10px', 
                  background: 'rgba(0,0,0,0.5)', 
                  padding: '2px',
                  borderRadius: '4px'
                }}>
                  {j}
                </div>
              </Html>
            </mesh>
          ))}
        </group>
      ))}
    </group>
  );
};

const CameraLogger = () => {
  const { camera } = useThree();
  return (
    <mesh visible={false} onClick={() => {
      const p = camera.position;
      const d = new THREE.Vector3();
      camera.getWorldDirection(d);
      const t = new THREE.Vector3().copy(p).add(d.multiplyScalar(5));
      console.log(`setLookAt(${p.x.toFixed(2)}, ${p.y.toFixed(2)}, ${p.z.toFixed(2)}, ${t.x.toFixed(2)}, ${t.y.toFixed(2)}, ${t.z.toFixed(2)}, true)`);
    }}>
      <boxGeometry args={[100, 100, 100]} />
      <meshBasicMaterial transparent opacity={0} />
    </mesh>
  )
}

const AnimationLoop = ({ walkRef, cameraControlRef, setIsWalking, WALK_SPEED, setWalkingText }) => {
  const currentLookDir = useRef(new THREE.Vector3()); 
  const isFirstFrame = useRef(true);

  const tempVec = useRef(new THREE.Vector3()); 
  const ROTATION_SPEED = 2.0;

  useFrame((state, delta) => {
    if (!walkRef.current.active || !cameraControlRef.current) return;

    const anim = walkRef.current;

    if (anim.segmentDuration === 0) {
      const dist = anim.currentStart.distanceTo(anim.currentEnd);
      
      const currentCamPos = cameraControlRef.current.camera.position;
      
      const moveDir = new THREE.Vector3().subVectors(anim.currentEnd, anim.currentStart).normalize();
      if (moveDir.lengthSq() === 0) moveDir.set(0, 0, -1);

      cameraControlRef.current.camera.getWorldDirection(tempVec.current);
      
      anim.lookStart = currentCamPos.clone().add(tempVec.current.multiplyScalar(10)); 
      anim.lookEnd = currentCamPos.clone().add(moveDir.multiplyScalar(10));           
      
      anim.segmentDuration = dist / WALK_SPEED; 
      anim.rotationDuration = 0.6; 
      
      anim.alpha = 0; 
      anim.phase = 'ROTATE'; 
    }

    if (anim.phase === 'ROTATE') {
      anim.alpha += delta / anim.rotationDuration;

      const currentLookTarget = new THREE.Vector3().lerpVectors(anim.lookStart, anim.lookEnd, Math.min(anim.alpha, 1));
      
      cameraControlRef.current.setLookAt(
        anim.currentStart.x, anim.currentStart.y, anim.currentStart.z,
        currentLookTarget.x, currentLookTarget.y, currentLookTarget.z,
        false
      );

      if (anim.alpha >= 1) {
        anim.phase = 'MOVE';
        anim.alpha = 0;
      }
      return; 
    }

    if (anim.phase === 'MOVE') {
      anim.alpha += delta / Math.max(anim.segmentDuration, 0.1);

      if (anim.alpha >= 1) {
        anim.pathQueue.shift(); 

        if (anim.pathQueue.length > 0) {
          anim.currentStart = anim.currentEnd;
          anim.currentEnd = anim.pathQueue[0];
          anim.segmentDuration = 0; 
          anim.alpha = 0;
        } else {
          anim.active = false;
          if (setIsWalking) setIsWalking(false);
          if (setWalkingText) setWalkingText(null);

          const cp = anim.currentEnd;

          const travelDir = new THREE.Vector3().subVectors(anim.currentEnd, anim.currentStart).normalize();
          
          if (travelDir.lengthSq() === 0) travelDir.set(0, 0, -1);

          const targetNatural = cp.clone().add(travelDir.multiplyScalar(0.01));

          cameraControlRef.current.setLookAt(
             cp.x, cp.y, cp.z, 
             targetNatural.x, targetNatural.y, targetNatural.z, 
             false
          );
        }
        return;
      }

      const nextPos = new THREE.Vector3().lerpVectors(anim.currentStart, anim.currentEnd, anim.alpha);
      
      const travelDir = new THREE.Vector3().subVectors(anim.currentEnd, anim.currentStart).normalize();
      const lookTargetMoving = nextPos.clone().add(travelDir.multiplyScalar(10));

      cameraControlRef.current.setLookAt(
        nextPos.x, nextPos.y, nextPos.z,
        lookTargetMoving.x, lookTargetMoving.y, lookTargetMoving.z,
        false 
      );
    }
  });

  return null;
};

  const LoadingScreen = ({ onFinished }) => {
    const { progress, active } = useProgress();
    const [finished, setFinished] = useState(false);

    useEffect(() => {
      if (!active && progress === 100) {
        const timer = setTimeout(() => {
          setFinished(true);
          if (onFinished) onFinished(); 
        }, 500);
        return () => clearTimeout(timer);
      } else {
        setFinished(false);
      }
  }, [active, progress, onFinished]);
  
  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background: '#000000',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      transition: 'opacity 1s ease-out',
      opacity: finished ? 0 : 1,
      pointerEvents: finished ? 'none' : 'all',
    }}>
      <div style={{
        fontSize: '24px',
        color: 'white',
        fontWeight: 'bold',
        marginBottom: '20px',
        letterSpacing: '2px'
      }}>
        CARGANDO MODELO 3D
      </div>
      
      <div style={{
        width: '200px',
        height: '4px',
        background: '#333',
        borderRadius: '2px',
        overflow: 'hidden'
      }}>
        <div style={{
          width: `${progress}%`,
          height: '100%',
          background: '#61dafb',
          transition: 'width 0.2s'
        }} />
      </div>
      
      <div style={{ color: '#888', marginTop: '10px', fontSize: '12px' }}>
        {progress.toFixed(0)}%
      </div>
    </div>
  );
};

const CollisionMesh = ({ url, cameraControlRef, testingMode, rotation = [0, 0, 0], position = [0, 0, 0] }) => {
  const obj = useLoader(OBJLoader, url);
  const lastValidPos = useRef(new THREE.Vector3());
  const lastValidTarget = useRef(new THREE.Vector3());
  const isInitialized = useRef(false);
  const raycaster = useRef(new THREE.Raycaster());

  useEffect(() => {
    if (obj && testingMode) {
      obj.traverse((child) => {
        if (child.isMesh) {
          child.material = new THREE.MeshBasicMaterial({ 
            color: 0xff0000, 
            wireframe: true, 
            transparent: true, 
            opacity: 0.3 
          });
        }
      });
    }
  }, [obj, testingMode]);

  useFrame(({ camera }) => {
    if (!cameraControlRef.current || !obj) return;
    
    const controls = cameraControlRef.current;
    const currentTarget = new THREE.Vector3();
    controls.getTarget(currentTarget);

    if (!isInitialized.current) {
      lastValidPos.current.copy(camera.position);
      lastValidTarget.current.copy(currentTarget);
      isInitialized.current = true;
      return;
    }

    const moveVec = new THREE.Vector3().subVectors(camera.position, lastValidPos.current);
    const distance = moveVec.length();

    if (distance > 0.001) {
      const dir = moveVec.clone().normalize();
      raycaster.current.set(lastValidPos.current, dir);

      const intersects = raycaster.current.intersectObject(obj, true);

      if (intersects.length > 0 && intersects[0].distance <= distance + 0.1) {
        controls.setLookAt(
          lastValidPos.current.x, lastValidPos.current.y, lastValidPos.current.z,
          lastValidTarget.current.x, lastValidTarget.current.y, lastValidTarget.current.z,
          false
        );
      } else {
        lastValidPos.current.copy(camera.position);
        lastValidTarget.current.copy(currentTarget);
      }
    }
  });

  return <primitive object={obj} visible={testingMode} rotation={rotation} position={position} />;
};

export default ModelViewer;